<template>
  <div class="detail-page">
    <button class="back-btn" @click="goBack"> ← Back to Applications </button>

    <div v-if="loading" class="state-message">Loading application…</div>
    <div v-else-if="!app" class="state-message">Application not found or you don’t have permission to view it.</div>

    <div v-else>
      <!-- Header Section -->
      <header class="detail-header">
        <h1 class="title">
          {{ app.positionapplying || app.position }}
        </h1>
        <div class="meta-cards">
          <div class="meta-card">
            <span class="meta-label">Applied</span>
            <span class="meta-value">
              {{ formatDate(app.submittedAt) }}
            </span>
          </div>
          <div class="meta-card status-card">
            <span class="meta-label">Status</span>
            <span :class="['meta-value', statusClass(app.status)]">
              {{ app.status }}
            </span>

          </div>
        </div>
      </header>
      <!-- Application Details -->


      <form @submit.prevent="handleSave" class="edit-form">
        <!-- PERSONAL INFORMATION -->
        <section class="form-section">
          <h2>Personal Information</h2>
          <div class="form-grid">
            <label>First Name: <input v-model="app.firstName" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Middle Name: <input v-model="app.middleName" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Last Name: <input v-model="app.lastName" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Date of Birth: <input v-model="app.dateOfBirth" type="date" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>SSN:
              <span class="ssn-wrapper">
                <input v-model="app.ssn" v-mask="'###-##-####'" :type="showSSN ? 'text' : 'password'" autocomplete="off"
                  :readonly="isFormReadOnly" :class="['ssn-input', { 'readonly-field': isFormReadOnly }]" />
                <button type="button" class="ssn-toggle" @click.prevent="showSSN = !showSSN"
                  aria-label="Toggle SSN visibility">
                  {{ showSSN ? 'Hide' : 'Show' }}
                </button>
              </span>
            </label>
            <label>Address: <input v-model="app.address" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>City: <input v-model="app.city" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>
              Country:
              <select v-model="app.country" class="w-full border rounded px-3 py-2" :disabled="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }">
                <option disabled value="">Select a country</option>
                <option>United States</option>
                <option>Canada</option>
                <option>Jamaica</option>
                <option>Costa Rica</option>
                <option>Columbia</option>
                <option>Honduras</option>
                <option>Ecuador</option>
                <option>Guatemala</option>
                <option>El Salvador</option>
                <option>Haiti</option>
                <option>Brazil</option>
                <option>Dominican Republic</option>
                <option>Mexico</option>
              </select>
            </label>

            <label>State: <input v-model="app.state" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Zip Code: <input v-model="app.zip" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Email: <input v-model="app.email" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Cell Phone: <input v-model="app.cell" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>WhatsApp: <input v-model="app.whatsapp" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
          </div>
        </section>

        <!-- CONTACT -->
        <section class="form-section">
          <h2>Contact & Emergency</h2>
          <div class="form-grid">

            <label>Emergency Contact Name: <input v-model="app.emergencyContactName" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Emergency Contact Phone: <input v-model="app.emergencyContactPhone" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
          </div>
        </section>

        <!-- EMPLOYMENT -->
        <section class="form-section">
          <h2>Position & Employment History</h2>
          <div class="form-grid">
            <label>Employer Name: <input v-model="app.employerName" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Work Phone: <input v-model="app.workPhone" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Start Date: <input v-model="app.dateEmployed" type="date" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>End Date: <input v-model="app.dateUnemployed" type="date" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Employer Address: <input v-model="app.employerAddress" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>City: <input v-model="app.employerCity" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>State: <input v-model="app.employerState" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Zip Code: <input v-model="app.employerZip" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Position Held: <input v-model="app.positionHeld" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Supervisor Name: <input v-model="app.supervisorName" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Duties Performed: <textarea v-model="app.dutiesPerformed" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Reason for Leaving: <textarea v-model="app.reasonForLeaving" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>May Contact Employer: <select v-model="app.mayContactEmployer" :disabled="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }">
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select></label>
          </div>
        </section>
        <section class="form-section">
          <h2>Additional Information</h2>
          <div class="form-grid">
            <label>Position Applying For: <input v-model="app.positionapplying" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Other Position: <input v-model="app.otherPosition" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Company Selected: <input v-model="app.companySelected" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Drug Test: <input v-model="app.drugTest" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Weekend Work: <input v-model="app.weekendWork" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Felony: <input v-model="app.felony" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
          </div>
        </section>

        <section class="form-section">
          <h2>Education History</h2>
          <div v-for="(edu, index) in app.education" :key="index" class="form-grid">
            <label>School Name: <input v-model="edu.schoolName" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>School Location: <input v-model="edu.schoolLocation" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Degree Received: <input v-model="edu.degreeReceived" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Major: <input v-model="edu.major" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Years Attended: <input v-model="edu.yearsAttended" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
          </div>
          <!--<div class="form-actions">
            <button type="button" @click="addEducation">Add Education</button>
            <button type="button" @click="removeEducation" :disabled="app.education.length <= 1">Remove Last</button>
          </div>-->
        </section>

        <section class="form-section">
          <h2>References</h2>
          <div class="form-grid">
            <label>Reference 1 Name: <input v-model="app.reference1Name" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Reference 1 Company: <input v-model="app.reference1Company" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Reference 1 Title: <input v-model="app.reference1Title" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Reference 1 Letter: <input type="file" @change="handleFileUpload($event, 'reference1')" :disabled="isFormReadOnly" /></label>
            <label>Reference 2 Name: <input v-model="app.reference2Name" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Reference 2 Company: <input v-model="app.reference2Company" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Reference 2 Title: <input v-model="app.reference2Title" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Reference 2 Letter: <input type="file" @change="handleFileUpload($event, 'reference2')" :disabled="isFormReadOnly" /></label>
          </div>
        </section>

        <section class="form-section">
          <h2>Passport & Signature</h2>
          <div class="form-grid">
            <label>Passport Number: <input v-model="app.passportnum" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Passport Expiration Date: <input v-model="app.passportexpirationdate" type="date" :readonly="isFormReadOnly" :class="{ 'readonly-field': isFormReadOnly }" /></label>
            <label>Upload Passport Image: <input type="file" @change="handleFileUpload($event, 'passport')" :disabled="isFormReadOnly" /></label>
            <label>Upload Resume: <input type="file" @change="handleFileUpload($event, 'resume')" :disabled="isFormReadOnly" /></label>
            <div>
              <strong>Signature Preview:</strong>
              <img :src="app.applicantSignatureDataUrl" alt="Signature" class="signature-img"
                @click="showSignatureModal = true" />
            </div>
          </div>
        </section>
        <!-- SAVE -->
        <div class="form-actions">
          <button type="submit" class="save-btn" :disabled="loading || isFormReadOnly">
            {{ loading ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { notifySuccess, notifyError } from '@/services/notify'
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { auth, db, storage } from '@/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import { doc, updateDoc, onSnapshot } from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import Swal from 'sweetalert2'


const route = useRoute()
const router = useRouter()
const app = ref(null)
const loading = ref(true)
const showSSN = ref(false)

const isFormReadOnly = computed(() => {
  const readOnlyStatuses = [
    'Accepted',
    'Interview Scheduled',
    'Interview Rejected',
    'Interview Accepted',
    'Agency Processing',
    'Converted to Employee'
  ]
  return readOnlyStatuses.includes(app.value?.status)
})

function goBack() {
  router.back()
}

function formatDate(ts) {
  if (!ts) return '—'
  const d = ts.toDate ? ts.toDate() : new Date(ts)
  return d.toLocaleString()
}

function statusClass(status) {
  return {
    'status-pill': true,
    'status-pending': status.toLowerCase() === 'pending',
    'status-approved': status.toLowerCase() === 'approved',
    'status-rejected': status.toLowerCase() === 'rejected',
    'status-interview-scheduled': status.toLowerCase() === 'interview scheduled',
    'status-interview-rejected': status.toLowerCase() === 'interview rejected',
    'status-interview-accepted': status.toLowerCase() === 'interview accepted',
    'status-agency-processing': status.toLowerCase() === 'agency processing',
    'status-converted': status.toLowerCase() === 'converted to employee'
  }
}

async function handleSave() {
  if (!app.value?.id) return;

  loading.value = true; // disable the button

  const appRef = doc(db, 'applications', app.value.id);
  const update = { ...app.value };
  delete update.id;

  await updateDoc(appRef, update);

  loading.value = false; // re-enable the button
  Swal.fire({
    icon: 'success',
    title: 'Application Updated',
    text: 'Your application has been successfully updated.',
    confirmButtonText: 'OK'
  });
}

async function handleFileUpload(event, fieldName) {
  const file = event.target.files[0];
  if (!file || !app.value?.id) return;

  const timestamp = Date.now();
  const path = `applications/${app.value.id}/${timestamp}_${file.name}`;
  const storageReference = storageRef(storage, path);

  try {
    await uploadBytes(storageReference, file);
    const url = await getDownloadURL(storageReference);
    app.value[fieldName] = url;
    await updateDoc(doc(db, 'applications', app.value.id), {
      [fieldName]: url
    });
    notifySuccess(`${fieldName} uploaded successfully.`, 'Uploaded');
  } catch (error) {
    console.error(`Error uploading ${fieldName}:`, error);
    notifyError(`${fieldName} could not be uploaded. Please try again.`, 'Upload failed');
  }
}


onMounted(() => {
  onAuthStateChanged(auth, (user) => {
    if (!user) {
      router.push({ name: 'Login', query: { redirect: route.fullPath } })
      return
    }

    const appRef = doc(db, 'applications', route.params.id)
    onSnapshot(appRef, (snap) => {
      if (!snap.exists()) {
        app.value = null
        return
      }
      const data = snap.data()
      if (data.removed || (data.email || '').trim().toLowerCase() !== (user.email || '').toLowerCase()) {
        app.value = null
        return
      }
      app.value = { id: snap.id, ...data }
      loading.value = false
    })
  })
})
</script>

<style scoped>
.ssn-wrapper {
  position: relative;
  display: block;
}

.ssn-wrapper .ssn-input {
  width: 100%;
  padding-right: 3.5rem;
}

.ssn-toggle {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
  cursor: pointer;
  border-radius: 0.25rem;
}

.ssn-toggle:hover {
  background: #f3f4f6;
  color: #374151;
}

.detail-page {
  max-width: 800px;
  margin: 2rem auto;
  padding: 1rem;
}

.back-btn {
  background: transparent;
  border: none;
  color: #4f46e5;
  cursor: pointer;
  margin-bottom: 1rem;
  font-weight: 500;
}

.detail-header {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff;
  padding: 2rem;
  border-radius: 0.75rem;
  margin-bottom: 2rem;
}

.detail-header .title {
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
}

.meta-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.meta-card {
  background: #fff;
  color: #1f2937;
  padding: 1rem 1.25rem;
  border-radius: 0.5rem;
  flex: 1;
  min-width: 160px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.meta-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 0.25rem;
  display: block;
}

.meta-value {
  font-weight: 600;
  font-size: 0.95rem;
}

.status-card .meta-value {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  text-transform: uppercase;
  background-color: #fbbf24;
  color: #1f2937;
}

.status-pending {
  background-color: #fbbf24 !important;
  color: #1f2937 !important;
}

.status-approved {
  background-color: #86efac !important;
  color: #1f2937 !important;
}

.status-rejected {
  background-color: #fca5a5 !important;
  color: #1f2937 !important;
}

.status-interview-scheduled {
  background-color: #93c5fd !important;
  color: #1f2937 !important;
}

.status-interview-rejected {
  background-color: #fda29b !important;
  color: #1f2937 !important;
}

.status-interview-accepted {
  background-color: #a7f3d0 !important;
  color: #1f2937 !important;
}

.status-agency-processing {
  background-color: #e9d5ff !important;
  color: #1f2937 !important;
}

.status-converted {
  background-color: #bfdbfe !important;
  color: #1f2937 !important;
}

.form-section {
  background: #f9fafb;
  padding: 1.5rem;
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #e5e7eb;
}

.form-section h2 {
  font-size: 1.2rem;
  margin-bottom: 1rem;
}

.form-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr 1fr;
}

.form-grid label {
  display: flex;
  flex-direction: column;
  font-size: 0.875rem;
}

.form-grid input,
.form-grid textarea,
.form-grid select {
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
}

.form-actions {
  text-align: right;
}

.save-btn {
  background-color: #4f46e5;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  border: none;
  font-weight: 600;
  cursor: pointer;
}

.readonly-field {
  background-color: #f3f4f6;
  /* light gray */
  color: #4b5563;
  /* dark gray text */
  border: 1px solid #d1d5db;
  cursor: not-allowed;
}
</style>
